/**
 * SlideShell — wrapper comum de slide.
 *
 * Aplica paddings, fundo de token, e posicionamento de elementos globais
 * (pílula do mestre). Cada componente de slide concreto (TextSlide,
 * QuizSlide, MissionSlide) fica dentro do children.
 *
 * Dark mode: `data-theme` aplicado aqui no wrapper pra ativar o override
 * do tokens.css. Ver DESIGN_TOKENS.md §Dark mode.
 */

import type { ReactNode } from 'react'

import { useLabTheme } from '../hooks/useLabTheme'

interface Props {
  children: ReactNode
  /** Variante "fullbleed" tira o container central (usado por missions ATLAS). */
  variant?: 'default' | 'fullbleed'
}

export function SlideShell({ children, variant = 'default' }: Props) {
  const { theme } = useLabTheme()
  const isDark = theme === 'dark'

  return (
    <div
      data-module-lab-root
      data-theme={theme}
      style={{
        minHeight: '100dvh',
        width: '100%',
        // Fallback literal conforme tema. Os tokens CSS existem, mas usar
        // literais aqui garante que o body.dark global do RPGIA não vaze
        // se a var não resolver.
        background: isDark ? '#0F1115' : '#FAFAF7',
        color: isDark ? '#F5F5F0' : '#0F1115',
        fontFamily: 'var(--font-lab-sans, system-ui, sans-serif)',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 72,
      }}
    >
      {variant === 'fullbleed' ? (
        <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      ) : (
        <div
          style={{
            flex: 1,
            width: '100%',
            maxWidth: 'min(960px, 100%)',
            margin: '0 auto',
            padding: 'var(--spacing-lab-4, 1rem) var(--spacing-lab-3, 0.875rem)',
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}
